import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Search, Briefcase, Upload, Brain, Zap } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JobFilters } from "./useJobMatching";

export interface JobMatchingSearchProps {
  onSearch: (searchQuery: string, resumeText: string, filters?: any) => void;
  isAnalyzing: boolean;
  onClear?: () => void;
  activeFilters: JobFilters;
}

export const JobMatchingSearch = ({ 
  onSearch, 
  isAnalyzing, 
  onClear,
  activeFilters 
}: JobMatchingSearchProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [resumeText, setResumeText] = useState("");
  const [searchMethod, setSearchMethod] = useState<"keywords" | "resume">("keywords");

  const handleSearch = () => {
    if (searchMethod === "keywords" && !searchQuery.trim()) {
      return;
    }
    
    if (searchMethod === "resume" && !resumeText.trim()) {
      return;
    }
    
    onSearch(
      searchQuery, 
      resumeText,
      activeFilters
    );
  };

  const handleClear = () => {
    setSearchQuery("");
    setResumeText("");
    if (onClear) {
      onClear();
    }
  };

  const getButtonState = () => {
    if (isAnalyzing) {
      return {
        disabled: true,
        text: "Analyzing...",
        icon: <Brain className="w-4 h-4 animate-pulse" />
      };
    }
    
    return {
      disabled: (searchMethod === "keywords" && !searchQuery.trim()) || 
                (searchMethod === "resume" && !resumeText.trim()),
      text: "Find Matches",
      icon: <Zap className="w-4 h-4" />
    };
  };

  const buttonState = getButtonState();

  return (
    <Card>
      <CardContent className="p-6">
        <Tabs defaultValue="keywords" onValueChange={(value) => setSearchMethod(value as "keywords" | "resume")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="keywords">
              <Search className="w-4 h-4 mr-2" />
              Search by Keywords
            </TabsTrigger>
            <TabsTrigger value="resume">
              <Upload className="w-4 h-4 mr-2" />
              Upload Resume
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="keywords" className="mt-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="keyword-search">Job Title, Skills, or Keywords</Label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="keyword-search"
                    placeholder="e.g. React Developer, Project Manager, Data Analyst"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Select defaultValue={activeFilters.location}>
                    <SelectTrigger id="location" className="mt-1.5">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      <SelectItem value="remote">Remote Only</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="onsite">On-site</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="job-type">Job Type</Label>
                  <Select defaultValue={activeFilters.jobType}>
                    <SelectTrigger id="job-type" className="mt-1.5">
                      <SelectValue placeholder="Select job type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="full-time">Full-time</SelectItem>
                      <SelectItem value="part-time">Part-time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="freelance">Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="resume" className="mt-4">
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="resume-text">Paste Your Resume</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs">
                    <Upload className="h-3.5 w-3.5 mr-1" /> Upload File
                  </Button>
                </div>
                <Textarea
                  id="resume-text"
                  placeholder="Paste your resume content here..."
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  className="h-32 mt-1.5"
                />
              </div>
              
              <div className="bg-blue-50 rounded p-3 text-sm text-blue-700">
                <Briefcase className="h-4 w-4 inline-block mr-2 text-blue-500" />
                Our AI will analyze your resume to find the best job matches
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      
      <Separator />
      
      <CardFooter className="p-4 flex justify-between">
        <Button variant="outline" onClick={handleClear} disabled={isAnalyzing}>
          Clear
        </Button>
        <Button onClick={handleSearch} disabled={buttonState.disabled} className="gap-1">
          {buttonState.icon}
          {buttonState.text}
        </Button>
      </CardFooter>
    </Card>
  );
};
